import { codeFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Link,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import { formatPrice } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import BatchOrderConfirmDialog from './BatchOrderConfirmDialog';

// Two order sources share one selection set: work-order parts by their numeric
// maintenance_part_id, lähete lines by a prefixed string key so the ids never collide.
export const saleLineKey = (line) => `ps-${line.parts_sale_line_id}`;
const partKey = (part) => part.maintenance_part_id;

/** Every selectable row key of one integration card, work orders first, then lähetteet. */
export const integrationRowKeys = (integration) => {
  const keys = [];
  (integration.maintenances || []).forEach((m) => m.parts.forEach((p) => keys.push(partKey(p))));
  (integration.parts_sales || []).forEach((s) => s.lines.forEach((l) => keys.push(saleLineKey(l))));
  return keys;
};

/** Distinct sources (work orders + lähetteet) across the queue — the tab badge. */
export const pendingSourceCount = (integrations) => {
  const workOrderIds = new Set();
  const saleIds = new Set();
  (integrations || []).forEach((int) => {
    (int.maintenances || []).forEach((m) => workOrderIds.add(m.maintenance_id));
    (int.parts_sales || []).forEach((s) => saleIds.add(s.parts_sale_id));
  });
  return workOrderIds.size + saleIds.size;
};

const rowTotal = (row) => (parseFloat(row.purchase_price) || 0) * (parseFloat(row.quantity) || 0);

function PendingSupplierPartsTab({ onPendingCountChange, onOrderCreated }) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Phones get a stacked row instead of the column grid: the code / quantity /
  // price columns do not survive a 360 px screen, they wrap into loose fragments.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedParts, setSelectedParts] = useState(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmIntegration, setConfirmIntegration] = useState(null);
  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [dismissingParts, setDismissingParts] = useState(new Set());

  const fetchPendingParts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get('/api/supplier-orders/pending-parts/');
      const list = resp.data.integrations || [];
      setIntegrations(list);
      setInventoryEnabled(resp.data.inventory_tracking_enabled || false);
      // Select everything by default
      const allKeys = new Set();
      list.forEach((int) => integrationRowKeys(int).forEach((k) => allKeys.add(k)));
      setSelectedParts(allKeys);
    } catch {
      setError(t('purchaseOrders.pendingParts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingParts();
  }, [fetchPendingParts]);

  // Notify parent of the number of pending sources (work orders + lähetteet)
  const totalPendingCount = useMemo(() => pendingSourceCount(integrations), [integrations]);

  useEffect(() => {
    if (onPendingCountChange) onPendingCountChange(totalPendingCount);
  }, [totalPendingCount, onPendingCountChange]);

  const toggleKey = (key) => {
    setSelectedParts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleKeys = (keys) => {
    const allSelected = keys.every((k) => selectedParts.has(k));
    setSelectedParts((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (allSelected) next.delete(k);
        else next.add(k);
      });
      return next;
    });
  };

  const toggleMaintenance = (maintenance) => toggleKeys(maintenance.parts.map(partKey));
  const togglePartsSale = (sale) => toggleKeys(sale.lines.map(saleLineKey));
  const toggleIntegration = (integration) => toggleKeys(integrationRowKeys(integration));

  const getSelectedForIntegration = (integration) => {
    const selected = [];
    (integration.maintenances || []).forEach((m) =>
      m.parts.forEach((p) => {
        if (selectedParts.has(partKey(p))) selected.push(p);
      })
    );
    return selected;
  };

  const getSelectedSaleLinesForIntegration = (integration) => {
    const selected = [];
    (integration.parts_sales || []).forEach((s) =>
      s.lines.forEach((l) => {
        if (selectedParts.has(saleLineKey(l))) selected.push(l);
      })
    );
    return selected;
  };

  const getSelectedMaintenancesForIntegration = (integration) =>
    (integration.maintenances || []).filter((m) => m.parts.some((p) => selectedParts.has(partKey(p))));

  const getSelectedPartsSalesForIntegration = (integration) =>
    (integration.parts_sales || []).filter((s) => s.lines.some((l) => selectedParts.has(saleLineKey(l))));

  const handleOrderClick = (integration) => {
    setConfirmIntegration(integration);
    setConfirmDialogOpen(true);
  };

  const handleOrderPlaced = () => {
    setConfirmDialogOpen(false);
    setConfirmIntegration(null);
    fetchPendingParts();
    if (onOrderCreated) onOrderCreated();
  };

  const handleStalePartsError = () => {
    setConfirmDialogOpen(false);
    setConfirmIntegration(null);
    fetchPendingParts();
  };

  // "Ohita tilaus": the row leaves the queue without an order. Payload names the
  // source (maintenance_part_id or parts_sale_line_id).
  const handleDismiss = async (key, payload, e) => {
    e.stopPropagation();
    setDismissingParts((prev) => new Set(prev).add(key));
    try {
      await axios.post('/api/supplier-orders/dismiss-pending-part/', payload);
      fetchPendingParts();
    } catch {
      setError(t('purchaseOrders.pendingParts.dismissFailed'));
    } finally {
      setDismissingParts((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
    );
  }

  if (integrations.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', mt: 2 }}>
        <LocalShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">
          {t('purchaseOrders.pendingParts.noPendingParts')}
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          {t('purchaseOrders.pendingParts.noPendingPartsDescription')}
        </Typography>
      </Paper>
    );
  }

  /** One selectable part row, shared by work-order parts and lähete lines. */
  const renderRow = (row, key, payload, testId) => {
    const dismissing = dismissingParts.has(key);
    const dismissButton = (
      <IconButton
        size="small"
        aria-label={t('purchaseOrders.pendingParts.dismiss')}
        onClick={(e) => handleDismiss(key, payload, e)}
        disabled={dismissing}
        sx={isMobile ? { mt: 0.25 } : { ml: 0.5 }}
      >
        {dismissing ? <CircularProgress size={16} /> : <CloseIcon fontSize="small" />}
      </IconButton>
    );
    if (isMobile) {
      /* Phone row: name on its own line, the numbers as one compact
         meta line underneath. Nothing is squeezed into a column. */
      return (
        <Box
          key={key}
          data-testid={testId}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            py: 0.25,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: '8px',
          }}
          onClick={() => toggleKey(key)}
        >
          <Checkbox
            checked={selectedParts.has(key)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleKey(key)}
            sx={{ p: 0.75 }}
          />
          <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 500, lineHeight: 1.3 }}>
              {row.part_name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, mt: '2px' }}>
              {row.article_id && (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', ...codeFont }}>
                  {row.article_id}
                </Typography>
              )}
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {formatQuantity(row.quantity)} {'×'}{' '}
                {row.purchase_price ? `${formatPrice(row.purchase_price)} €` : '-'}
              </Typography>
              {inventoryEnabled && row.stock_info && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: row.stock_info.is_low_stock ? 'warning.main' : 'text.secondary',
                    fontWeight: row.stock_info.is_low_stock ? 600 : 400,
                  }}
                >
                  {t('purchaseOrders.pendingParts.stock')}: {formatQuantity(row.stock_info.quantity_in_stock)}
                </Typography>
              )}
            </Box>
          </Box>
          {dismissButton}
        </Box>
      );
    }
    return (
      <Box
        key={key}
        data-testid={testId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 3,
          py: 0.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: '6px',
        }}
        onClick={() => toggleKey(key)}
      >
        <Checkbox
          checked={selectedParts.has(key)}
          size="small"
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleKey(key)}
        />
        <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 500, minWidth: 100 }}>
          {row.part_name}
        </Typography>
        <Typography sx={{ width: 100, fontSize: '12px', color: 'text.secondary', textAlign: 'right' }}>
          {row.article_id || '-'}
        </Typography>
        <Typography sx={{ width: 50, fontSize: '13px', textAlign: 'right' }}>
          {formatQuantity(row.quantity)}
        </Typography>
        {inventoryEnabled && (
          <Typography
            sx={{
              width: 70,
              fontSize: '13px',
              textAlign: 'right',
              color: row.stock_info?.is_low_stock ? 'warning.main' : 'text.secondary',
              fontWeight: row.stock_info?.is_low_stock ? 600 : 400,
            }}
          >
            {row.stock_info ? formatQuantity(row.stock_info.quantity_in_stock) : '-'}
          </Typography>
        )}
        <Typography sx={{ width: 80, fontSize: '13px', textAlign: 'right' }}>
          {row.purchase_price ? `${formatPrice(row.purchase_price)} €` : '-'}
        </Typography>
        <Tooltip title={t('purchaseOrders.pendingParts.dismiss')}>
          <span>{dismissButton}</span>
        </Tooltip>
      </Box>
    );
  };

  const renderColumnHeader = () => (
    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, pl: 5, mb: 0.5 }}>
      <Typography sx={{ flex: 1, fontSize: '11px', color: 'text.secondary', fontWeight: 600 }}>
        {t('purchaseOrders.pendingParts.partName')}
      </Typography>
      <Typography sx={{ width: 100, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
        {t('purchaseOrders.articleCode')}
      </Typography>
      <Typography sx={{ width: 50, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
        {t('purchaseOrders.quantity')}
      </Typography>
      {inventoryEnabled && (
        <Typography sx={{ width: 70, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
          {t('purchaseOrders.pendingParts.stock')}
        </Typography>
      )}
      <Typography sx={{ width: 80, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
        {t('purchaseOrders.purchasePrice')}
      </Typography>
      <Box sx={{ width: 36 }} />
    </Box>
  );

  const renderSubtotal = (selectedCount, total) =>
    selectedCount > 0 && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 0.5 }}>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          {t('purchaseOrders.pendingParts.partsTotal')}: {formatPrice(total)} {'€'}
        </Typography>
      </Box>
    );

  const groupSx = { px: { xs: 1, sm: 2 }, py: 1.5, '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' } };

  return (
    <Box>
      {integrations.map((integration) => {
        const allKeys = integrationRowKeys(integration);
        const selectedCount = allKeys.filter((k) => selectedParts.has(k)).length;
        const allSelected = selectedCount === allKeys.length;

        const selectedTotal =
          getSelectedForIntegration(integration).reduce((sum, p) => sum + rowTotal(p), 0) +
          getSelectedSaleLinesForIntegration(integration).reduce((sum, l) => sum + rowTotal(l), 0);

        return (
          <Paper key={integration.integration_id} elevation={0} sx={{ mb: 3 }}>
            {/* Integration header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 1.5, sm: 2 },
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
                <Checkbox
                  checked={allSelected && allKeys.length > 0}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={() => toggleIntegration(integration)}
                  size="small"
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                    {integration.integration_name}
                  </Typography>
                  {integration.supplier_type === 'email' && integration.order_email && (
                    <Typography
                      sx={{ fontSize: '12px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
                      data-testid="pending-email-recipient"
                    >
                      <EmailOutlinedIcon sx={{ fontSize: 14 }} />
                      {t('purchaseOrders.emailRecipientLine', { email: integration.order_email })}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={t('purchaseOrders.pendingParts.selectedCount', { count: selectedCount })}
                  size="small"
                  color={selectedCount > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<ShoppingCartCheckoutIcon />}
                disabled={selectedCount === 0}
                onClick={() => handleOrderClick(integration)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {t('purchaseOrders.pendingParts.orderSelected')}
                {selectedCount > 0 && ` (${formatPrice(selectedTotal)} €)`}
              </Button>
            </Box>

            <Divider />

            {/* Maintenances */}
            {(integration.maintenances || []).map((maintenance) => {
              const mKeys = maintenance.parts.map(partKey);
              const mSelectedCount = mKeys.filter((k) => selectedParts.has(k)).length;
              const mAllSelected = mSelectedCount === mKeys.length;
              const mTotal = maintenance.parts.reduce(
                (sum, p) => (selectedParts.has(partKey(p)) ? sum + rowTotal(p) : sum),
                0
              );

              return (
                <Box key={`m-${maintenance.maintenance_id}`} sx={groupSx}>
                  {/* Maintenance header — on a phone the vehicle and the customer
                      drop to their own line so the work number and plate stay
                      readable instead of wrapping into stray fragments. */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Checkbox
                      checked={mAllSelected && mKeys.length > 0}
                      indeterminate={mSelectedCount > 0 && !mAllSelected}
                      onChange={() => toggleMaintenance(maintenance)}
                      size="small"
                      sx={isMobile ? { p: 0.75 } : undefined}
                    />
                    {isMobile ? (
                      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Link
                            component={RouterLink}
                            to={`/maintenances/${maintenance.maintenance_id}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '14px' }}
                          >
                            #{maintenance.work_number}
                            <LaunchIcon sx={{ fontSize: 14 }} />
                          </Link>
                          {maintenance.vehicle_registration && (
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.secondary' }}>
                              {maintenance.vehicle_registration}
                            </Typography>
                          )}
                        </Box>
                        {(maintenance.vehicle_info || maintenance.customer_name) && (
                          <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', lineHeight: 1.35 }}>
                            {[maintenance.vehicle_info, maintenance.customer_name].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        <Link
                          component={RouterLink}
                          to={`/maintenances/${maintenance.maintenance_id}`}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '14px' }}
                        >
                          #{maintenance.work_number}
                          <LaunchIcon sx={{ fontSize: 14 }} />
                        </Link>
                        {maintenance.vehicle_registration && (
                          <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                            {maintenance.vehicle_registration}
                          </Typography>
                        )}
                        {maintenance.vehicle_info && (
                          <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                            {maintenance.vehicle_info}
                          </Typography>
                        )}
                        {maintenance.customer_name && (
                          <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                            {'—'} {maintenance.customer_name}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {renderColumnHeader()}
                  {maintenance.parts.map((part) =>
                    renderRow(
                      part,
                      partKey(part),
                      { maintenance_part_id: part.maintenance_part_id },
                      `pending-part-${part.maintenance_part_id}`
                    )
                  )}
                  {renderSubtotal(mSelectedCount, mTotal)}
                </Box>
              );
            })}

            {/* Lähetteet (delivery notes with to_order lines) */}
            {(integration.parts_sales || []).map((sale) => {
              const sKeys = sale.lines.map(saleLineKey);
              const sSelectedCount = sKeys.filter((k) => selectedParts.has(k)).length;
              const sAllSelected = sSelectedCount === sKeys.length;
              const sTotal = sale.lines.reduce(
                (sum, l) => (selectedParts.has(saleLineKey(l)) ? sum + rowTotal(l) : sum),
                0
              );

              return (
                <Box key={`s-${sale.parts_sale_id}`} sx={groupSx} data-testid={`pending-parts-sale-${sale.parts_sale_id}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Checkbox
                      checked={sAllSelected && sKeys.length > 0}
                      indeterminate={sSelectedCount > 0 && !sAllSelected}
                      onChange={() => togglePartsSale(sale)}
                      size="small"
                      sx={isMobile ? { p: 0.75 } : undefined}
                    />
                    <Link
                      component={RouterLink}
                      to={`/lahetteet/${sale.parts_sale_id}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '14px' }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 16 }} />
                      {t('purchaseOrders.pendingParts.deliveryNote', { number: sale.number })}
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                    {sale.customer_name && (
                      <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                        {'—'} {sale.customer_name}
                      </Typography>
                    )}
                  </Box>

                  {renderColumnHeader()}
                  {sale.lines.map((line) =>
                    renderRow(
                      line,
                      saleLineKey(line),
                      { parts_sale_line_id: line.parts_sale_line_id },
                      `pending-sale-line-${line.parts_sale_line_id}`
                    )
                  )}
                  {renderSubtotal(sSelectedCount, sTotal)}
                </Box>
              );
            })}
          </Paper>
        );
      })}

      {/* Batch order confirmation dialog */}
      {confirmIntegration && (
        <BatchOrderConfirmDialog
          open={confirmDialogOpen}
          onClose={() => { setConfirmDialogOpen(false); setConfirmIntegration(null); }}
          integration={confirmIntegration}
          selectedPartIds={getSelectedForIntegration(confirmIntegration).map((p) => p.maintenance_part_id)}
          selectedMaintenances={getSelectedMaintenancesForIntegration(confirmIntegration)}
          selectedParts={getSelectedForIntegration(confirmIntegration)}
          selectedSaleLineIds={getSelectedSaleLinesForIntegration(confirmIntegration).map((l) => l.parts_sale_line_id)}
          selectedPartsSales={getSelectedPartsSalesForIntegration(confirmIntegration)}
          selectedSaleLines={getSelectedSaleLinesForIntegration(confirmIntegration)}
          onOrderPlaced={handleOrderPlaced}
          onStalePartsError={handleStalePartsError}
          inventoryEnabled={inventoryEnabled}
        />
      )}
    </Box>
  );
}

export default PendingSupplierPartsTab;
